import React, { useState } from 'react';
import { CalendarIcon, ClockIcon, CheckCircleIcon, RepeatIcon, ArrowRightIcon, BellIcon, DatabaseIcon, RefreshCwIcon, AlertCircleIcon, FileTextIcon, PlusIcon, Settings2Icon, ArrowUpCircleIcon, ArrowDownCircleIcon, CheckSquareIcon, XCircleIcon, ClipboardListIcon, ServerIcon } from 'lucide-react';
export const WorkflowSettings = () => {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  return <div className="space-y-8" data-id="element-1790">
      {/* Billing & Assessment Workflows */}
      <div className="border-b border-gray-200 pb-8" data-id="element-1791">
        <h3 className="text-base font-semibold text-gray-900 mb-6" data-id="element-1792">
          Billing & Assessment Workflows
        </h3>
        <div className="space-y-6" data-id="element-1793">
          {/* Billing Frequency */}
          <div data-id="element-1794">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1795">
              Billing Frequency Configuration
            </label>
            <div className="space-y-4" data-id="element-1796">
              {[{
              type: 'Property Tax',
              frequency: 'Annual',
              cycles: [{
                month: 'January',
                description: 'Main Billing Run'
              }, {
                month: 'July',
                description: 'Supplementary Billing'
              }],
              status: 'Active'
            }, {
              type: 'Utility Services',
              frequency: 'Monthly',
              cycles: [{
                month: 'Every 1st',
                description: 'Water & Waste'
              }, {
                month: 'Every 15th',
                description: 'Other Services'
              }],
              status: 'Active'
            }].map(config => <div key={config.type} className="border border-gray-200 rounded-lg p-4" data-id="element-1797">
                  <div className="flex justify-between items-center mb-3" data-id="element-1798">
                    <div data-id="element-1799">
                      <h4 className="text-sm font-medium text-gray-900" data-id="element-1800">
                        {config.type}
                      </h4>
                      <p className="text-xs text-gray-500" data-id="element-1801">
                        Frequency: {config.frequency}
                      </p>
                    </div>
                    <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full" data-id="element-1802">
                      {config.status}
                    </span>
                  </div>
                  <div className="space-y-2" data-id="element-1803">
                    {config.cycles.map(cycle => <div key={cycle.month} className="flex items-center text-sm" data-id="element-1804">
                        <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-1805" />
                        <span className="text-gray-600" data-id="element-1806">{cycle.month}:</span>
                        <span className="text-gray-900 ml-2" data-id="element-1807">
                          {cycle.description}
                        </span>
                      </div>)}
                  </div>
                </div>)}
            </div>
          </div>
          {/* Bill Generation Automation */}
          <div data-id="element-1808">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1809">
              Bill Generation Automation
            </label>
            <div className="space-y-4" data-id="element-1810">
              {[{
              phase: 'Pre-Generation Validation',
              timing: '3 days before',
              tasks: ['Data Completeness Check', 'Rate Verification', 'Exception Review']
            }, {
              phase: 'Generation Process',
              timing: 'Scheduled Date',
              tasks: ['Generate Bills', 'Apply Early Payment Discounts', 'Format for Distribution']
            }, {
              phase: 'Post-Generation QC',
              timing: '1 day after',
              tasks: ['Sample Verification', 'Total Amount Reconciliation', 'Exception Handling']
            }].map(phase => <div key={phase.phase} className="border border-gray-200 rounded-lg p-4" data-id="element-1811">
                  <div className="flex items-center justify-between mb-3" data-id="element-1812">
                    <h4 className="text-sm font-medium text-gray-900" data-id="element-1813">
                      {phase.phase}
                    </h4>
                    <span className="text-xs text-gray-500" data-id="element-1814">
                      {phase.timing}
                    </span>
                  </div>
                  <div className="space-y-2" data-id="element-1815">
                    {phase.tasks.map((task, index) => <div key={index} className="flex items-center text-sm text-gray-600" data-id="element-1816">
                        <CheckSquareIcon className="h-4 w-4 text-blue-500 mr-2" data-id="element-1817" />
                        {task}
                      </div>)}
                  </div>
                </div>)}
            </div>
          </div>
          {/* Reassessment Configuration */}
          <div data-id="element-1818">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1819">
              Reassessment Cycle Configuration
            </label>
            <div className="space-y-4" data-id="element-1820">
              {[{
              type: 'Regular Reassessment',
              frequency: 'Every 3 years',
              triggers: ['Cycle Due Date', 'Major Market Changes', 'Council Resolution']
            }, {
              type: 'Special Reassessment',
              frequency: 'Event-based',
              triggers: ['Property Improvement', 'Zoning Change', 'Owner Appeal']
            }].map(config => <div key={config.type} className="border border-gray-200 rounded-lg p-4" data-id="element-1821">
                  <div className="flex items-center justify-between mb-3" data-id="element-1822">
                    <h4 className="text-sm font-medium text-gray-900" data-id="element-1823">
                      {config.type}
                    </h4>
                    <span className="text-xs text-gray-500" data-id="element-1824">
                      {config.frequency}
                    </span>
                  </div>
                  <div className="space-y-2" data-id="element-1825">
                    {config.triggers.map((trigger, index) => <div key={index} className="flex items-center text-sm text-gray-600" data-id="element-1826">
                        <RepeatIcon className="h-4 w-4 text-purple-500 mr-2" data-id="element-1827" />
                        {trigger}
                      </div>)}
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
      {/* Appeal & Exemption Workflows */}
      <div className="border-b border-gray-200 pb-8" data-id="element-1828">
        <h3 className="text-base font-semibold text-gray-900 mb-6" data-id="element-1829">
          Appeal & Exemption Workflows
        </h3>
        <div className="space-y-6" data-id="element-1830">
          {/* Appeal Workflow */}
          <div data-id="element-1831">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1832">
              Appeal Process Configuration
            </label>
            <div className="space-y-4" data-id="element-1833">
              {[{
              stage: 'Initial Review',
              deadline: '5 business days',
              assignee: 'Assessment Officer',
              actions: ['Validate Documentation', 'Check Eligibility', 'Route to Assessor']
            }, {
              stage: 'Technical Assessment',
              deadline: '10 business days',
              assignee: 'Senior Assessor',
              actions: ['Review Property Details', 'Compare Market Data', 'Prepare Recommendation']
            }, {
              stage: 'Final Decision',
              deadline: '5 business days',
              assignee: 'Assessment Committee',
              actions: ['Review Recommendation', 'Vote on Appeal', 'Issue Decision']
            }].map(stage => <div key={stage.stage} className="border border-gray-200 rounded-lg p-4" data-id="element-1834">
                  <div className="flex items-center justify-between mb-3" data-id="element-1835">
                    <h4 className="text-sm font-medium text-gray-900" data-id="element-1836">
                      {stage.stage}
                    </h4>
                    <div className="flex items-center" data-id="element-1837">
                      <ClockIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-1838" />
                      <span className="text-xs text-gray-500" data-id="element-1839">
                        {stage.deadline}
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 mb-2" data-id="element-1840">
                    Assigned to: {stage.assignee}
                  </p>
                  <div className="space-y-2" data-id="element-1841">
                    {stage.actions.map((action, index) => <div key={index} className="flex items-center text-sm text-gray-600" data-id="element-1842">
                        <ArrowRightIcon className="h-4 w-4 text-blue-500 mr-2" data-id="element-1843" />
                        {action}
                      </div>)}
                  </div>
                </div>)}
            </div>
          </div>
          {/* Exemption Workflow */}
          <div data-id="element-1844">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1845">
              Exemption Application Workflow
            </label>
            <div className="space-y-4" data-id="element-1846">
              {[{
              type: 'Senior Citizen Exemption',
              requirements: ['Age Proof', 'Income Statement', 'Property Ownership'],
              approvalLevels: ['Document Verification', 'Income Qualification', 'Final Approval']
            }, {
              type: 'Religious Institution',
              requirements: ['Registration Certificate', 'Property Use Declaration', 'Public Benefit Proof'],
              approvalLevels: ['Document Verification', 'Site Inspection', 'Council Approval']
            }].map(exemption => <div key={exemption.type} className="border border-gray-200 rounded-lg p-4" data-id="element-1847">
                  <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-1848">
                    {exemption.type}
                  </h4>
                  <div className="space-y-3" data-id="element-1849">
                    <div data-id="element-1850">
                      <p className="text-xs text-gray-500 mb-2" data-id="element-1851">
                        Required Documents:
                      </p>
                      <div className="space-y-1" data-id="element-1852">
                        {exemption.requirements.map((req, index) => <div key={index} className="flex items-center text-sm text-gray-600" data-id="element-1853">
                            <FileTextIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-1854" />
                            {req}
                          </div>)}
                      </div>
                    </div>
                    <div data-id="element-1855">
                      <p className="text-xs text-gray-500 mb-2" data-id="element-1856">
                        Approval Flow:
                      </p>
                      <div className="space-y-1" data-id="element-1857">
                        {exemption.approvalLevels.map((level, index) => <div key={index} className="flex items-center text-sm text-gray-600" data-id="element-1858">
                            <ArrowRightIcon className="h-4 w-4 text-green-500 mr-2" data-id="element-1859" />
                            {level}
                          </div>)}
                      </div>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
      {/* Payment & Refund Workflows */}
      <div className="border-b border-gray-200 pb-8" data-id="element-1860">
        <h3 className="text-base font-semibold text-gray-900 mb-6" data-id="element-1861">
          Payment & Refund Workflows
        </h3>
        <div className="space-y-6" data-id="element-1862">
          {/* Payment Processing */}
          <div data-id="element-1863">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1864">
              Payment Processing Workflows
            </label>
            <div className="space-y-4" data-id="element-1865">
              {[{
              type: 'Payment Reversal',
              steps: ['Validation of Reversal Request', 'Account Impact Assessment', 'Supervisor Approval', 'Transaction Reversal', 'Customer Notification'],
              timeframe: '1-2 business days'
            }, {
              type: 'Refund Processing',
              steps: ['Eligibility Verification', 'Amount Calculation', 'Documentation Review', 'Finance Approval', 'Payment Processing'],
              timeframe: '5-7 business days'
            }].map(workflow => <div key={workflow.type} className="border border-gray-200 rounded-lg p-4" data-id="element-1866">
                  <div className="flex items-center justify-between mb-3" data-id="element-1867">
                    <h4 className="text-sm font-medium text-gray-900" data-id="element-1868">
                      {workflow.type}
                    </h4>
                    <span className="text-xs text-gray-500" data-id="element-1869">
                      {workflow.timeframe}
                    </span>
                  </div>
                  <div className="space-y-2" data-id="element-1870">
                    {workflow.steps.map((step, index) => <div key={index} className="flex items-center text-sm text-gray-600" data-id="element-1871">
                        <ArrowRightIcon className="h-4 w-4 text-blue-500 mr-2" data-id="element-1872" />
                        {step}
                      </div>)}
                  </div>
                </div>)}
            </div>
          </div>
          {/* Penalty/Interest Waivers */}
          <div data-id="element-1873">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1874">
              Penalty/Interest Waiver Authority
            </label>
            <div className="space-y-4" data-id="element-1875">
              {[{
              level: 'Supervisor',
              limit: 'Up to 5,000 ETB',
              conditions: ['First Time Request', 'Valid Reason', 'Good Payment History']
            }, {
              level: 'Manager',
              limit: 'Up to 25,000 ETB',
              conditions: ['Documented Hardship', 'Partial Payment Made', 'Appeal Committee Review']
            }, {
              level: 'Director',
              limit: 'Above 25,000 ETB',
              conditions: ['Board Approval', 'Legal Review', 'Full Documentation']
            }].map(authority => <div key={authority.level} className="border border-gray-200 rounded-lg p-4" data-id="element-1876">
                  <div className="flex items-center justify-between mb-3" data-id="element-1877">
                    <h4 className="text-sm font-medium text-gray-900" data-id="element-1878">
                      {authority.level}
                    </h4>
                    <span className="text-xs text-blue-600 font-medium" data-id="element-1879">
                      {authority.limit}
                    </span>
                  </div>
                  <div className="space-y-2" data-id="element-1880">
                    {authority.conditions.map((condition, index) => <div key={index} className="flex items-center text-sm text-gray-600" data-id="element-1881">
                        <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" data-id="element-1882" />
                        {condition}
                      </div>)}
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
      {/* System Automation & Maintenance */}
      <div className="pt-8" data-id="element-1883">
        <h3 className="text-base font-semibold text-gray-900 mb-6" data-id="element-1884">
          System Automation & Maintenance
        </h3>
        <div className="space-y-6" data-id="element-1885">
          {/* Automated Notifications */}
          <div data-id="element-1886">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1887">
              Notification Configuration
            </label>
            <div className="space-y-4" data-id="element-1888">
              {[{
              event: 'Bill Due Reminder',
              timing: ['15 days before', '7 days before', '1 day before'],
              channels: ['Email', 'SMS', 'Portal']
            }, {
              event: 'Payment Confirmation',
              timing: ['Immediate'],
              channels: ['Email', 'SMS', 'Portal']
            }, {
              event: 'Late Payment Notice',
              timing: ['1 day after', '7 days after', '30 days after'],
              channels: ['Email', 'SMS', 'Portal', 'Print']
            }].map(notification => <div key={notification.event} className="border border-gray-200 rounded-lg p-4" data-id="element-1889">
                  <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-1890">
                    {notification.event}
                  </h4>
                  <div className="space-y-3" data-id="element-1891">
                    <div data-id="element-1892">
                      <p className="text-xs text-gray-500 mb-2" data-id="element-1893">Timing:</p>
                      <div className="space-y-1" data-id="element-1894">
                        {notification.timing.map((time, index) => <div key={index} className="flex items-center text-sm text-gray-600" data-id="element-1895">
                            <ClockIcon className="h-4 w-4 text-orange-500 mr-2" data-id="element-1896" />
                            {time}
                          </div>)}
                      </div>
                    </div>
                    <div data-id="element-1897">
                      <p className="text-xs text-gray-500 mb-2" data-id="element-1898">Channels:</p>
                      <div className="flex flex-wrap gap-2" data-id="element-1899">
                        {notification.channels.map(channel => <span key={channel} className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded" data-id="element-1900">
                            {channel}
                          </span>)}
                      </div>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
          {/* System Maintenance */}
          <div data-id="element-1901">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1902">
              System Maintenance Schedule
            </label>
            <div className="space-y-4" data-id="element-1903">
              {[{
              task: 'Data Backup',
              schedule: 'Daily at 00:00',
              retention: '30 days',
              type: 'Incremental'
            }, {
              task: 'Full System Backup',
              schedule: 'Weekly on Sunday',
              retention: '12 months',
              type: 'Full'
            }, {
              task: 'Data Archival',
              schedule: 'Yearly',
              retention: '7 years',
              type: 'Compressed Archive'
            }].map(maintenance => <div key={maintenance.task} className="border border-gray-200 rounded-lg p-4" data-id="element-1904">
                  <div className="flex items-center justify-between mb-3" data-id="element-1905">
                    <h4 className="text-sm font-medium text-gray-900" data-id="element-1906">
                      {maintenance.task}
                    </h4>
                    <span className="text-xs text-purple-600" data-id="element-1907">
                      {maintenance.type}
                    </span>
                  </div>
                  <div className="space-y-2" data-id="element-1908">
                    <div className="flex items-center text-sm text-gray-600" data-id="element-1909">
                      <RepeatIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-1910" />
                      Schedule: {maintenance.schedule}
                    </div>
                    <div className="flex items-center text-sm text-gray-600" data-id="element-1911">
                      <ClockIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-1912" />
                      Retention: {maintenance.retention}
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </div>;
};